import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, User, Mail, Globe, Building, Settings, Shield, LogOut, Eye,
  Loader2, ChevronRight, Download, Clock, Zap, FileText, CheckCircle, Save,
  Search, BarChart3, Target, GitCompare, Bot, Wrench, ShoppingCart, BookOpen, Building2,
  AlertTriangle, Trash2, Menu, X, Home, Layout, Gift, Lock, Sparkles, Code2
} from "lucide-react";
import MyDomainsTab from "../components/dashboard/MyDomainsTab";
import BlogAgentKPITab from "../components/dashboard/BlogAgentKPITab";
import InventoryAgentTab from "../components/dashboard/InventoryAgentTab";
import ConnectedStoresTab from "../components/dashboard/ConnectedStoresTab";
import Phase3AgentsTab from "../components/dashboard/Phase3AgentsTab";
import ServicesShowcase from "../components/dashboard/ServicesShowcase";
import AddonsTab from "../components/dashboard/AddonsTab";
import BlogCalendarTab from "../components/dashboard/BlogCalendarTab";
import BlogPacksPanel from "../components/dashboard/BlogPacksPanel";
import ReferralTab from "../components/dashboard/ReferralTab";
import PartnerWidgetTab from "../components/dashboard/PartnerWidgetTab";
import AgentApiKeysTab from "../components/dashboard/AgentApiKeysTab";
import CatalogBrainTab from "../components/dashboard/CatalogBrainTab";
import ChatbotWidget from "../components/ChatbotWidget";
import NotificationsTab from "../components/dashboard/NotificationsTab";
import CustomBrandPanel from "../components/dashboard/CustomBrandPanel";
import KeywordResearchTab from "../components/dashboard/KeywordResearchTab";
import SEOScoreTab from "../components/dashboard/SEOScoreTab";
import DashboardActivityWidget from "../components/dashboard/DashboardActivityWidget";
import CitationTierPanel from "../components/snapshot/CitationTierPanel";
import EshopUsageWidget from "../components/EshopUsageWidget";
import SubscriptionManagementCard from "../components/dashboard/SubscriptionManagementCard";
import DashboardSidebar from "../components/dashboard/DashboardSidebar";
import { AdminDashboard } from "../components/admin/AdminDashboard";

export default function DashboardPage() {
  const { user, logout, updateProfile } = useAuth();
  const { t, language } = useLanguage();
  const initialTab = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("tab") || "overview")
    : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [adminAutoSet, setAdminAutoSet] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [editWebsite, setEditWebsite] = useState("");
  const [billing, setBilling] = useState({ billing_name: "", billing_ico: "", billing_dic: "", billing_ic_dph: "", billing_address: "", billing_city: "", billing_zip: "", billing_country: "" });
  const [saving, setSaving] = useState(false);
  const [monitoring, setMonitoring] = useState(null);
  const [blogAgents, setBlogAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFAMsg, setTwoFAMsg] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // SIDEBAR: Main items always visible, rest collapsible
  const MAIN_ITEMS = [
    { id: "overview", label: t.sb_overview || "Overview", icon: Home },
    { id: "domains", label: t.dm_title || "My Domains", icon: Layout },
    { id: "articles", label: t.cal_title || "Content Calendar", icon: FileText },
    { id: "blog-agents-kpi", label: t.sb_blog_kpi || "Blog Agents KPI", icon: Bot, badge: "LIVE", badgeColor: "bg-emerald-100 text-emerald-700" },
    { id: "inventory-agent", label: t.sb_inventory || "Inventory Agent", icon: ShoppingCart, badge: "NEW", badgeColor: "bg-orange-100 text-orange-700" },
    { id: "connected-stores", label: t.sb_connected_stores || "Connected Stores", icon: ShoppingCart, badge: "NEW", badgeColor: "bg-violet-100 text-violet-700" },
    { id: "catalog-brain", label: t.sb_catalog_brain || "Catalog Brain", icon: Sparkles, badge: "AI", badgeColor: "bg-emerald-100 text-emerald-700" },
    { id: "phase3-agents", label: t.sb_phase3 || "Phase 3 Agents", icon: Bot, badge: "NEW", badgeColor: "bg-fuchsia-100 text-fuchsia-700" },
    { id: "blog-packs", label: "Blog Packs", icon: FileText, badge: "€25+", badgeColor: "bg-indigo-100 text-indigo-700" },
    { id: "keywords", label: t.kw_title || "Keyword Research", icon: Search },
    { id: "seo-score", label: t.ss_title || "SEO/GEO Score", icon: BarChart3 },
    { id: "citations", label: "AI Citations", icon: Bot, badge: "PRO", badgeColor: "bg-purple-100 text-purple-700" },
    { id: "eshop-scanner", label: "E-shop SEO Scanner", icon: ShoppingCart, badge: "NEW", badgeColor: "bg-emerald-100 text-emerald-700" },
    { id: "agency", label: "Agency Portal", icon: Building2, badge: "AGENCY", badgeColor: "bg-purple-100 text-purple-700" },
    { id: "referral", label: t.ref_title || "Earn Free Service", icon: Gift, highlight: true },
    { id: "partner-widget", label: t.sb_partner_widget || "Embed Widget", icon: Code2, badge: "AFF", badgeColor: "bg-emerald-100 text-emerald-700" },
  ];

  const COLLAPSIBLE_SECTIONS = [
    {
      key: "account", label: t.sb_account || "Account",
      items: [
        { id: "addons", label: t.ao_tab || "Add-ons", icon: Zap },
        { id: "brand", label: "Brand your AI™", icon: Sparkles, badge: "ENT", badgeColor: "bg-amber-100 text-amber-700" },
        { id: "notifications", label: t.notif_title || "Notifications", icon: Eye },
        { id: "profile", label: t.sb_profile || "Profile", icon: User },
        { id: "security", label: t.sb_security || "Security", icon: Shield },
        { id: "settings", label: t.sb_settings || "Settings", icon: Settings },
      ],
    },
    {
      key: "tools", label: t.sb_ai_tools || "AI Tools",
      items: [
        { id: "scan", label: t.sb_new_scan || "New SEO Scan", icon: Search, href: "/", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "simulator", label: t.sb_simulator || "AI Simulator", icon: Bot, href: "/ai-simulator", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "fix-seo", label: t.sb_fix_seo || "Fix My SEO", icon: Wrench, href: "/fix-my-seo", badge: "€9", badgeColor: "bg-amber-100 text-amber-700" },
        { id: "cookie-audit", label: t.sb_cookie_audit || "Cookie/GDPR Audit", icon: Shield, href: "/cookie-audit", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
      ],
    },
    {
      key: "monitoring", label: t.sb_monitoring || "Monitoring",
      items: [
        { id: "brand-monitor", label: t.sb_brand_monitor || "AI Brand Monitor", icon: Eye, href: "/brand-monitor", badge: "€19/mo", badgeColor: "bg-blue-100 text-blue-700" },
        { id: "competitor-spy", label: t.sb_competitor_spy || "Competitor Spy", icon: GitCompare, href: "/products/competitor-spy", badge: "€29/mo", badgeColor: "bg-blue-100 text-blue-700" },
        { id: "attribution", label: t.sb_attribution || "AI Attribution", icon: BarChart3, href: "/attribution", badge: "€39/mo", badgeColor: "bg-blue-100 text-blue-700" },
        { id: "autopilot", label: t.sb_autopilot || "SEO Autopilot", icon: Zap, href: "/products/seo-autopilot", badge: "€79/mo", badgeColor: "bg-purple-100 text-purple-700" },
      ],
    },
    {
      key: "content", label: t.sb_content || "Content",
      items: [
        { id: "blog-agent", label: t.sb_blog_agent || "Blog Agent", icon: BookOpen, href: "/products/blog-agent", badge: "€25", badgeColor: "bg-amber-100 text-amber-700" },
        { id: "blog", label: t.sb_blog || "Blog", icon: FileText, href: "/blog" },
      ],
    },
    {
      key: "plugins", label: t.sb_plugins || "Plugins",
      items: [
        { id: "api-keys", label: "AI Agenti & API Keys", icon: Zap, badge: "NEW", badgeColor: "bg-purple-100 text-purple-700" },
        { id: "wordpress", label: "WordPress", icon: Download, href: "/plugin", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "woocommerce", label: "WooCommerce", icon: ShoppingCart, href: "/plugin#woocommerce", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "shopify", label: "Shopify Embed", icon: ShoppingCart, href: "/plugin#shopify", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "webflow", label: "Webflow CMS", icon: Globe, href: "/plugin#webflow", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "wix", label: "Wix", icon: Globe, href: "/plugin#wix", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "webhook", label: "Webhook API", icon: Zap, href: "/plugin#webhooks", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
        { id: "widget", label: "SEO Widget", icon: Target, href: "/products/wordpress-plugin", badge: "FREE", badgeColor: "bg-emerald-100 text-emerald-700" },
      ],
    },
    {
      key: "browse", label: t.sb_browse || "Browse",
      items: [
        { id: "local-seo", label: t.sb_local_seo || "Local SEO Pages", icon: Globe, href: "/seo/en" },
        { id: "products", label: t.sb_all_products || "All Products", icon: Shield, href: "/products/ai-visibility" },
      ],
    },
  ];

  useEffect(() => {
    if (user?.role === "admin" && !adminAutoSet) {
      // Respect explicit ?tab= URL param — only auto-set "admin" if no tab specified
      const urlTab = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("tab")
        : null;
      if (!urlTab) {
        setActiveTab("admin");
      }
      setAdminAutoSet(true);
    }
  }, [user, adminAutoSet]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      try {
        const [profRes, monRes, blogRes] = await Promise.all([
          axios.get(`${API}/auth/profile`, { withCredentials: true }),
          axios.get(`${API}/monitoring/dashboard?email=${encodeURIComponent(user.email)}`),
          axios.get(`${API}/blog-agent/status?email=${encodeURIComponent(user.email)}`)
        ]);
        setProfile(profRes.data);
        setMonitoring(monRes.data);
        setBlogAgents(blogRes.data.agents || []);
        setEditName(profRes.data.name || "");
        setEditCompany(profRes.data.company || "");
        setEditWebsite(profRes.data.website || "");
        setBilling({
          billing_name: profRes.data.billing_name || "",
          billing_ico: profRes.data.billing_ico || "",
          billing_dic: profRes.data.billing_dic || "",
          billing_ic_dph: profRes.data.billing_ic_dph || "",
          billing_address: profRes.data.billing_address || "",
          billing_city: profRes.data.billing_city || "",
          billing_zip: profRes.data.billing_zip || "",
          billing_country: profRes.data.billing_country || "",
        });
        try {
          const tfaRes = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true });
          setTwoFAStatus(tfaRes.data.enabled);
        } catch {}
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({ name: editName, company: editCompany, website: editWebsite, ...billing });
      const res = await axios.get(`${API}/auth/profile`, { withCredentials: true });
      setProfile(res.data);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { withCredentials: true });
      await logout();
      window.location.href = "/";
    } catch (e) { console.error(e); }
    setDeleting(false);
  };

  const planLabels = { free: t.plan_free || "Free", starter: t.plan_pro || "Pro", pro: t.plan_pro || "Pro", business: t.plan_business || "Business", enterprise: t.plan_enterprise || "Enterprise" };
  const planColors = { free: "bg-gray-100 text-gray-700", starter: "bg-blue-100 text-blue-700", pro: "bg-blue-100 text-blue-700", business: "bg-purple-100 text-purple-700", enterprise: "bg-yellow-100 text-yellow-700" };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="dashboard-page">
      <DashboardSidebar
        user={user}
        t={t}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mainItems={MAIN_ITEMS}
        collapsibleSections={COLLAPSIBLE_SECTIONS}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        logout={logout}
      />

      <main className="flex-1 min-w-0">
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-sidebar-btn"><Menu className="w-6 h-6 text-gray-600" /></button>
          <span className="font-bold font-['Chivo']">TopRankPulse</span>
          <button onClick={logout} className="text-gray-400"><LogOut className="w-5 h-5" /></button>
        </header>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          {/* Overview */}
          {activeTab === "overview" && (
            <div className="space-y-6" data-testid="dashboard-overview">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-14 h-14 rounded-full ring-2 ring-purple-500/30" data-testid="dash-avatar" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center text-white text-xl font-black" data-testid="dash-avatar-initial">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="text-2xl sm:text-3xl font-black font-['Chivo']" data-testid="dash-welcome">
                    {(t.dash_welcome || "Vitaj, {name}!").replace("{name}", user.name || user.email.split("@")[0])}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {t.dash_welcome_sub || "Toto je tvoj domovský panel. Spusti skeny, sleduj viditeľnosť a rozbehni kampane."}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 p-5">
                  <Globe className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="text-2xl font-bold font-mono">{monitoring?.total_sites || 0}</p>
                  <p className="text-xs text-gray-500">{t.dash_monitored || "Monitored Sites"}</p>
                </div>
                <div className="bg-white border border-gray-200 p-5">
                  <Zap className="w-5 h-5 text-green-600 mb-2" />
                  <p className="text-2xl font-bold font-mono">{monitoring?.avg_score || 0}</p>
                  <p className="text-xs text-gray-500">{t.dash_avg_score || "Avg SEO Score"}</p>
                </div>
                <div className="bg-white border border-gray-200 p-5">
                  <Shield className="w-5 h-5 text-red-600 mb-2" />
                  <p className="text-2xl font-bold font-mono">{monitoring?.total_alerts || 0}</p>
                  <p className="text-xs text-gray-500">{t.dash_alerts || "Active Alerts"}</p>
                </div>
                <div className="bg-white border border-gray-200 p-5">
                  <Clock className="w-5 h-5 text-purple-600 mb-2" />
                  <p className="text-2xl font-bold font-mono">{profile?.stats?.total_analyses || 0}</p>
                  <p className="text-xs text-gray-500">{t.dash_total_analyses || "Total Analyses"}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-6">
                <ServicesShowcase t={t} user={user} />
              </div>

              <DashboardActivityWidget userEmail={user.email} />

              {/* E-shop SEO/GEO usage — quota progress bars or upgrade
                  CTA when user has no eshop plan. Self-contained: pulls
                  fresh data on mount + polls every 60s. */}
              <EshopUsageWidget userEmail={user.email} />

              {monitoring?.sites?.length > 0 && (
                <div className="bg-white border border-gray-200 p-6">
                  <h3 className="font-bold text-lg font-['Chivo'] mb-4">{t.dash_sites || "Monitored Sites"}</h3>
                  <div className="space-y-3">
                    {monitoring.sites.map((site, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100">
                        <div>
                          <p className="font-medium text-sm">{site.url}</p>
                          <p className="text-xs text-gray-500">{t.dash_last_scan || "Last scan"}: {site.last_scan ? new Date(site.last_scan).toLocaleDateString() : (t.dash_never || 'Never')}</p>
                        </div>
                        <span className={`px-3 py-1 text-sm font-bold ${site.score >= 80 ? 'bg-green-100 text-green-700' : site.score >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {site.score || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* My Domains */}
          {activeTab === "domains" && <MyDomainsTab user={user} t={t} />}

          {/* Add-ons */}
          {activeTab === "addons" && <AddonsTab />}

          {/* Referral Program */}
          {activeTab === "referral" && <ReferralTab />}
          {activeTab === "partner-widget" && <PartnerWidgetTab user={user} />}
          {activeTab === "api-keys" && <AgentApiKeysTab />}

          {/* Notifications Settings */}
          {activeTab === "notifications" && <NotificationsTab />}

          {/* Brand your own PulseAI (Enterprise) */}
          {activeTab === "brand" && <CustomBrandPanel />}

          {/* Admin Panel */}
          {activeTab === "admin" && user?.role === "admin" && <AdminDashboard t={t} language={language} onBack={() => setActiveTab("overview")} />}

          {/* Articles — Calendar View */}
          {activeTab === "articles" && <BlogCalendarTab user={user} blogAgents={blogAgents} />}
          {activeTab === "blog-agents-kpi" && <BlogAgentKPITab user={user} t={t} />}
          {activeTab === "inventory-agent" && <InventoryAgentTab user={user} t={t} />}
          {activeTab === "connected-stores" && <ConnectedStoresTab user={user} t={t} />}
          {activeTab === "catalog-brain" && <CatalogBrainTab user={user} />}
          {activeTab === "phase3-agents" && <Phase3AgentsTab user={user} t={t} />}
          {activeTab === "blog-packs" && <BlogPacksPanel />}

          {/* Keyword Research */}
          {activeTab === "keywords" && <KeywordResearchTab user={user} />}

          {/* SEO/GEO Score */}
          {activeTab === "seo-score" && <SEOScoreTab user={user} />}

          {/* AI Citations Pro tier panel */}
          {activeTab === "citations" && (
            <div data-testid="dashboard-citations-tab">
              <CitationTierPanel
                defaultDomain={profile?.website || ""}
                onUpgradeClick={() => (window.location.href = "/products/ai-visibility")}
              />
            </div>
          )}

          {/* Agency Portal — opens the standalone page */}
          {activeTab === "agency" && (
            <div data-testid="dashboard-agency-tab" className="p-6 bg-white border border-gray-200 rounded-sm">
              <h2 className="text-2xl font-black mb-2">Agency Portal</h2>
              <p className="text-sm text-gray-600 mb-4 max-w-2xl">
                Multi-client mode pre agentúry: CSV import, bulk SEO scan, bulk GEO page
                generator a white-label PDF reporty. Vyžaduje addon Agency Portal alebo
                plán eshop_business.
              </p>
              <a
                href="/agency"
                data-testid="dashboard-agency-open"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm"
              >
                <Building2 className="w-4 h-4" />
                Otvoriť Agency Portal →
              </a>
            </div>
          )}

          {/* E-shop Scanner — opens the standalone page in a new tab to
              keep the dashboard chrome clean. The page itself runs the
              scan + report drill-down. */}
          {activeTab === "eshop-scanner" && (
            <div data-testid="dashboard-eshop-scanner-tab" className="p-6 bg-white border border-gray-200 rounded-sm">
              <h2 className="text-2xl font-black mb-2">E-shop SEO/GEO Scanner</h2>
              <p className="text-sm text-gray-600 mb-4 max-w-2xl">
                Bulk audit celého e-shopu cez sitemap — per-URL známka A–F, top issues
                a copy-paste AI fix pre každý problém. Naskenuj 10 až 500 URL na jeden klik.
              </p>
              <a
                href="/eshop-scanner"
                data-testid="dashboard-eshop-scanner-open"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                Otvoriť E-shop Scanner →
              </a>
            </div>
          )}

          {/* Profile */}
          {activeTab === "profile" && (
            <div className="max-w-2xl space-y-6" data-testid="dashboard-profile">
              <h2 className="text-2xl font-black font-['Chivo']">{t.prof_title || "Profile"}</h2>
              <div className="bg-white border border-gray-200 p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.prof_name || "Name"}</label>
                  <input data-testid="profile-name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder={t.prof_name_ph || "Your name"}
                    className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.prof_email || "Email"}</label>
                  <input value={user.email} disabled className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 text-gray-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.prof_company || "Company"}</label>
                  <input data-testid="profile-company" value={editCompany} onChange={(e) => setEditCompany(e.target.value)} placeholder={t.prof_company_ph || "Your company"}
                    className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.prof_website || "Website"}</label>
                  <input data-testid="profile-website" value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} placeholder="https://yourwebsite.com"
                    className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none" />
                </div>

                {/* Billing / Fakturačné údaje */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">{t.bill_title || "Billing Details"}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_name || "Company / Name"}</label>
                      <input data-testid="billing-name" value={billing.billing_name} onChange={e => setBilling(b => ({...b, billing_name: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_ico || "Company ID (ICO)"}</label>
                      <input data-testid="billing-ico" value={billing.billing_ico} onChange={e => setBilling(b => ({...b, billing_ico: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_dic || "Tax ID (DIC)"}</label>
                      <input data-testid="billing-dic" value={billing.billing_dic} onChange={e => setBilling(b => ({...b, billing_dic: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_icdph || "VAT ID (IC DPH)"}</label>
                      <input data-testid="billing-icdph" value={billing.billing_ic_dph} onChange={e => setBilling(b => ({...b, billing_ic_dph: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_address || "Address"}</label>
                      <input data-testid="billing-address" value={billing.billing_address} onChange={e => setBilling(b => ({...b, billing_address: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_city || "City"}</label>
                      <input data-testid="billing-city" value={billing.billing_city} onChange={e => setBilling(b => ({...b, billing_city: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_zip || "ZIP / Postal Code"}</label>
                      <input data-testid="billing-zip" value={billing.billing_zip} onChange={e => setBilling(b => ({...b, billing_zip: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t.bill_country || "Country"}</label>
                      <input data-testid="billing-country" value={billing.billing_country} onChange={e => setBilling(b => ({...b, billing_country: e.target.value}))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none" />
                    </div>
                  </div>
                </div>

                <button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white font-semibold hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t.prof_save || "Save Changes"}
                </button>
              </div>
            </div>
          )}

          {/* Security */}
          {activeTab === "security" && (
            <div className="max-w-2xl space-y-6" data-testid="dashboard-security">
              <h2 className="text-2xl font-black font-['Chivo']">{t.sec_title || "Security"}</h2>

              <div className="bg-white border border-gray-200 p-6">
                <h3 className="font-bold text-lg font-['Chivo'] mb-4">{t.sec_change_pwd || "Change Password"}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.sec_current || "Current Password"}</label>
                    <input data-testid="current-password" type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                      placeholder={t.sec_current_ph || "Current password"} className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.sec_new || "New Password"}</label>
                    <input data-testid="new-password" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                      placeholder={t.sec_new_ph || "New password (min 6 characters)"} className="w-full px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none" />
                  </div>
                  {pwdMsg && <p className={`text-sm ${pwdMsg.includes(t.sec_pwd_changed || 'success') ? 'text-green-600' : 'text-red-600'}`}>{pwdMsg}</p>}
                  <button data-testid="change-password-btn" disabled={pwdSaving || !newPwd}
                    onClick={async () => {
                      setPwdSaving(true); setPwdMsg("");
                      try {
                        await axios.post(`${API}/auth/change-password`, { current_password: currentPwd, new_password: newPwd }, { withCredentials: true });
                        setPwdMsg(t.sec_pwd_changed || "Password changed successfully"); setCurrentPwd(""); setNewPwd("");
                      } catch (e) { setPwdMsg(e.response?.data?.detail || (t.sec_pwd_failed || "Failed to change password")); }
                      setPwdSaving(false);
                    }}
                    className="px-6 py-2.5 bg-blue-600 text-white font-semibold hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2">
                    {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} {t.sec_change_btn || "Change Password"}
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-6" data-testid="2fa-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg font-['Chivo']">{t.sec_2fa_title || "Two-Factor Authentication (2FA)"}</h3>
                  <span className={`px-3 py-1 text-xs font-bold ${twoFAStatus ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {twoFAStatus ? (t.sec_enabled || 'Enabled') : (t.sec_disabled || 'Disabled')}
                  </span>
                </div>

                {!twoFAStatus ? (
                  <>
                    <p className="text-sm text-gray-500 mb-4">{t.sec_2fa_desc || "Add an extra layer of security. Use Google Authenticator, Authy, or any TOTP app."}</p>
                    {!twoFASetup ? (
                      <button data-testid="setup-2fa-btn"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
                            setTwoFASetup(r.data);
                          } catch {}
                        }}
                        className="px-6 py-2.5 bg-blue-600 text-white font-semibold hover:bg-blue-500 flex items-center gap-2">
                        <Shield className="w-4 h-4" /> {t.sec_enable_2fa || "Enable 2FA"}
                      </button>
                    ) : (
                      <div className="space-y-4" data-testid="2fa-setup-form">
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700 mb-3">{t.sec_scan_qr || "Scan this QR code with your authenticator app:"}</p>
                          <img src={twoFASetup.qr_code} alt="2FA QR Code" className="mx-auto w-48 h-48 border border-gray-200 p-2" data-testid="2fa-qr-code" />
                          <p className="text-xs text-gray-400 mt-2 font-mono break-all">Secret: {twoFASetup.secret}</p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 p-3">
                          <p className="text-xs font-bold text-yellow-700 mb-1">{t.sec_backup || "Backup Codes (save these!):"}</p>
                          <div className="flex flex-wrap gap-2">
                            {twoFASetup.backup_codes?.map((code, i) => (
                              <span key={i} className="font-mono text-xs bg-white px-2 py-1 border border-yellow-300">{code}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t.sec_enter_code || "Enter code from your app:"}</label>
                          <div className="flex gap-2">
                            <input data-testid="2fa-verify-code" type="text" value={twoFACode} onChange={e => setTwoFACode(e.target.value)}
                              placeholder={t.sec_6digit || "6-digit code"} maxLength={6} className="flex-1 px-4 py-2.5 border border-gray-300 focus:border-blue-500 focus:outline-none font-mono text-center text-lg tracking-widest" />
                            <button data-testid="2fa-verify-btn"
                              onClick={async () => {
                                setTwoFAMsg("");
                                try {
                                  await axios.post(`${API}/auth/2fa/verify`, { code: twoFACode }, { withCredentials: true });
                                  setTwoFAStatus(true); setTwoFASetup(null); setTwoFACode(""); setTwoFAMsg(t.sec_2fa_activated || "2FA activated!");
                                } catch (e) { setTwoFAMsg(e.response?.data?.detail || (t.sec_invalid_code || "Invalid code")); }
                              }}
                              className="px-6 py-2.5 bg-green-600 text-white font-bold hover:bg-green-500">
                              {t.sec_verify || "Verify"}
                            </button>
                          </div>
                          {twoFAMsg && <p className={`text-sm mt-2 ${twoFAMsg.includes(t.sec_2fa_activated || 'activated') ? 'text-green-600' : 'text-red-600'}`}>{twoFAMsg}</p>}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 border border-green-200">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">{t.sec_2fa_active || "2FA is active. Your account has an extra layer of protection."}</p>
                    </div>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">{t.sec_disable_label || "Enter code to disable:"}</label>
                        <input data-testid="2fa-disable-code" type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)}
                          placeholder={t.sec_disable_ph || "6-digit code or backup code"} className="w-full px-4 py-2 border border-gray-300 focus:border-purple-500 focus:outline-none font-mono" />
                      </div>
                      <button data-testid="disable-2fa-btn"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/auth/2fa/disable`, { code: disableCode }, { withCredentials: true });
                            setTwoFAStatus(false); setDisableCode("");
                          } catch (e) { alert(e.response?.data?.detail || (t.sec_invalid_code || "Invalid code")); }
                        }}
                        className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50">
                        {t.sec_disable_btn || "Disable 2FA"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="max-w-2xl space-y-6" data-testid="dashboard-settings">
              <h2 className="text-2xl font-black font-['Chivo']">{t.set_title || "Settings"}</h2>

              <div className="bg-white border border-gray-200 p-6">
                <h3 className="font-bold text-lg font-['Chivo'] mb-4">{t.set_subscription || "Subscription"}</h3>
                <div className="flex items-center gap-4 mb-4">
                  <span className={`px-4 py-2 text-sm font-bold ${planColors[user.plan] || planColors.free}`}>
                    {planLabels[user.plan] || planLabels.free} {t.set_plan || "Plan"}
                  </span>
                </div>

                {/* Plan Features */}
                <div className="bg-gray-50 border border-gray-100 p-4 mb-4 space-y-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{t.dm_plan_includes || "Your plan includes"}</p>
                  {user.plan === "free" && (
                    <>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 1 {t.dm_domain_label || "domain"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.dm_basic_seo || "Basic SEO scan"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> AI Simulator (FREE)</p>
                    </>
                  )}
                  {user.plan === "pro" && (
                    <>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 5 {t.dm_domains_label || "domains"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.pf2 || "Weekly AI reports"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.pf3 || "AI Citation Tracking"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.pf5 || "Keyword tracking (500)"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.pf7 || "PDF reports"}</p>
                    </>
                  )}
                  {user.plan === "business" && (
                    <>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 20 {t.dm_domains_label || "domains"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.bf2 || "Real-time daily monitoring"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.bf3 || "White-label reports"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.bf4 || "Unlimited keywords"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.bf6 || "Team collaboration"}</p>
                    </>
                  )}
                  {user.plan === "enterprise" && (
                    <>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.ef1 || "Unlimited websites"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.ef2 || "Custom AI models"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.ef3 || "Full API access"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.ef4 || "SLA guarantee"}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> {t.ef8 || "Dedicated account manager"}</p>
                    </>
                  )}
                </div>

                {user.plan === "free" && (
                  <div className="bg-blue-50 border border-blue-200 p-4">
                    <p className="text-sm font-medium text-blue-800 mb-2">{t.set_upgrade || "Upgrade to unlock all features"}</p>
                    <p className="text-xs text-blue-600 mb-3">{t.set_upgrade_desc || "Monitor 5 websites, AI reports, competitor tracking, and more."}</p>
                    <a href="/#pricing" className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500">{t.set_view_plans || "View Plans"}</a>
                  </div>
                )}
              </div>

              {/* V166.1 — Manage subscription (Stripe Customer Portal) */}
              <SubscriptionManagementCard t={t} />

              <div className="bg-white border border-gray-200 p-6">
                <h3 className="font-bold text-lg font-['Chivo'] mb-4">{t.set_account || "Account"}</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>{t.set_email || "Email"}: <span className="font-medium text-gray-800">{user.email}</span></p>
                  <p>{t.set_member || "Member since"}: <span className="font-medium text-gray-800">{new Date(user.created_at).toLocaleDateString("sk-SK")}</span></p>
                  <p>{t.set_role || "Role"}: <span className="font-medium text-gray-800">{user.role}</span></p>
                  <p>{t.set_user_id || "User ID"}: <span className="font-mono text-xs text-gray-400">{user.user_id}</span></p>
                </div>
              </div>

              <div className="bg-white border border-red-200 p-6" data-testid="delete-account-section">
                <h3 className="font-bold text-lg text-red-600 font-['Chivo'] mb-2">{t.del_title || "Delete Account"}</h3>
                <p className="text-sm text-gray-500 mb-4">{t.del_desc || "This will permanently delete your account, all monitored sites, reports, and data. This action cannot be undone."}</p>
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 flex items-center gap-2" data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4" /> {t.del_btn || "Delete My Account"}
                  </button>
                ) : (
                  <div className="bg-red-50 border border-red-200 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 font-medium">{t.del_confirm || "Are you sure? All your data will be permanently deleted."}</p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={handleDeleteAccount} disabled={deleting}
                        className="px-4 py-2 bg-purple-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="confirm-delete-btn">
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {t.del_yes || "Yes, Delete Everything"}
                      </button>
                      <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 border border-gray-300 text-gray-600 text-sm hover:bg-gray-50">
                        {t.del_cancel || "Cancel"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <ChatbotWidget mode="dashboard" user={user} onAction={(tab) => setActiveTab(tab)} />
    </div>
  );
}
